// ============================================================
// ConditionModifierForm - the Add-Condition picker's custom modifier row
// ============================================================
// A signed modifier on one scope (every test, a characteristic, a skill,
// or movement on the map), with optional rounds and - asked each time -
// what it does to one already worn on the same scope (v8.367.2). It
// applies to the target's tests and reactions when the "Apply condition
// modifiers" house rule is on. What the row emits is `customModifierEntry`.

import { useState, type KeyboardEvent } from 'react';
import { STACK_POLICIES, isStackPolicy, type StackPolicy } from '../engine/stackPolicy';
import { MOVEMENT_SCOPE } from '../engine/conditionMovement';
import { SKILL_TEST_DEFS, CHARACTERISTIC_TEST_LABELS } from '../engine/characteristicTests';
import type { CharacteristicId } from '../engine/character/types';
import { getViewerPref, setViewerPref } from '../utils/viewerPrefs';
import { customModifierEntry, type CustomModifierEntry } from './conditionPicker';
import common from '../styles/common.module.css';
import styles from '../styles/AddConditionModal.module.css';

/** The 9 characteristics, in sheet order, for the custom-modifier scope selector. */
const CHARACTERISTIC_SCOPES: { id: CharacteristicId; label: string }[] = (
  ['ws', 'bs', 'str', 'tgh', 'agi', 'int', 'per', 'wp', 'fel'] as CharacteristicId[]
).map((id) => ({ id, label: CHARACTERISTIC_TEST_LABELS[id] }));

const HINT_TIMED = 'Negative = penalty, positive = benefit. Optional duration in rounds; blank = permanent.';
const HINT_TIMELESS = 'Negative = penalty, positive = benefit. It lasts while they stand here.';

interface ConditionModifierFormProps {
  /** `opts.stacking` is the picker's own ruling; absent = the session's. */
  onAdd: (conditionCode: string, opts?: { stacking?: StackPolicy }) => void;
  /** No rounds and no policy: it lasts while they stand here. */
  timeless: boolean;
}

export function ConditionModifierForm({ onAdd, timeless }: ConditionModifierFormProps) {
  const [scope, setScope] = useState('all');
  const [value, setValue] = useState('');
  const [rounds, setRounds] = useState('');
  // The last answer is remembered per viewer; 'session' defers to the table's ruling.
  const [policy, setPolicy] = useState<StackPolicy | 'session'>(() => {
    const last = getViewerPref('stackPolicyLast');
    return isStackPolicy(last) ? last : 'session';
  });
  const [cap, setCap] = useState('');

  const add = () => {
    const entry = customModifierEntry({ scope, value, rounds, policy, cap, timeless });
    if (!entry) return;
    emit(entry, onAdd);
    setValue('');
    setRounds('');
    setCap('');
  };
  const addOnEnter = (e: KeyboardEvent) => e.key === 'Enter' && add();

  return (
    <div className={styles.customPenalty}>
      <div className={styles.categoryHeader}>Custom Modifier</div>
      <div className={styles.customRow}>
        <ScopeSelect scope={scope} onChange={setScope} />
        <input
          className={styles.penValInput}
          type="number"
          step={5}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="±"
          aria-label="Modifier value"
          onKeyDown={addOnEnter}
        />
        {!timeless && (
          <input
            className={styles.penDurInput}
            type="number"
            min={1}
            value={rounds}
            onChange={(e) => setRounds(e.target.value)}
            placeholder="Rds"
            aria-label="Modifier duration"
            onKeyDown={addOnEnter}
          />
        )}
        <button className={common.btnSmall} onClick={add} type="button">
          + Add
        </button>
      </div>
      {!timeless && <PolicyRow policy={policy} onPolicy={setPolicy} cap={cap} onCap={setCap} />}
      <div className={styles.penHint}>{timeless ? HINT_TIMELESS : HINT_TIMED}</div>
    </div>
  );
}

/** Remember the answer (when there is one to remember), then add - in that order. */
function emit(entry: CustomModifierEntry, onAdd: ConditionModifierFormProps['onAdd']) {
  if (entry.remember !== undefined) setViewerPref('stackPolicyLast', entry.remember);
  if (entry.stacking) onAdd(entry.code, { stacking: entry.stacking });
  else onAdd(entry.code);
}

// Markup only: the three option groups are the whole of it.
function ScopeSelect({ scope, onChange }: { scope: string; onChange: (scope: string) => void }) {
  return (
    <select
      className={styles.penScopeSelect}
      value={scope}
      onChange={(e) => onChange(e.target.value)}
      aria-label="Modifier scope"
    >
      <optgroup label="General">
        <option value="all">All tests</option>
        {/* Not a test at all: metres on the map. Its own scope
            rather than Agility, because "−10 to movement" says a
            combatant is slower, not clumsier. */}
        <option value={MOVEMENT_SCOPE}>Movement (map)</option>
      </optgroup>
      <optgroup label="Characteristics">
        {CHARACTERISTIC_SCOPES.map((c) => (
          <option key={c.id} value={c.id}>
            {c.label}
          </option>
        ))}
      </optgroup>
      <optgroup label="Skills">
        {SKILL_TEST_DEFS.map((s) => (
          <option key={s.id} value={s.id}>
            {s.label}
          </option>
        ))}
      </optgroup>
    </select>
  );
}

interface PolicyRowProps {
  policy: StackPolicy | 'session';
  onPolicy: (policy: StackPolicy | 'session') => void;
  cap: string;
  onCap: (cap: string) => void;
}

/** "If it is already there" - and, under raise only, the cap it never passes. */
function PolicyRow({ policy, onPolicy, cap, onCap }: PolicyRowProps) {
  return (
    <div className={styles.customRow}>
      <select
        className={styles.penScopeSelect}
        value={policy}
        onChange={(e) => onPolicy(e.target.value as StackPolicy | 'session')}
        aria-label="If it is already there"
        title="What this modifier does to one already worn on the same scope"
      >
        <option value="session">If already there: the table's ruling</option>
        {STACK_POLICIES.map((p) => (
          <option key={p.key} value={p.key} title={p.hint}>
            If already there: {p.label}
          </option>
        ))}
      </select>
      {policy === 'raise' && (
        <input
          className={styles.penDurInput}
          type="number"
          step={5}
          value={cap}
          onChange={(e) => onCap(e.target.value)}
          placeholder="Cap"
          aria-label="Raise cap"
          title="The total never passes this, in the value's own direction"
        />
      )}
    </div>
  );
}
